import type { HexColor, LucideIconName, MockCache, UserGroup, UserGroupId, UserGroupMember, UserId, UserRole } from '../../types';

import { isManager } from '../../knowledge/tree';
import { systemGroupRole } from '../../user-groups';
import { bump, requireCache } from '../runtime';

import { setUserRole } from './people';
import { sortKeyAfter, sortKeyLast } from './tree';

export const USER_GROUPS_KEY = 'user-groups';

const MAX_GROUP_NAME_LENGTH = 120;

function requireManager(cache: MockCache): void {
  if (!isManager(cache)) {
    throw new Error('Это действие доступно менеджеру или администратору');
  }
}

function requireGroupName(raw: string): string {
  const name = raw.trim();
  if (name.length === 0) {
    throw new Error('Название группы не может быть пустым');
  }
  if (name.length > MAX_GROUP_NAME_LENGTH) {
    throw new Error(`Название группы длиннее ${MAX_GROUP_NAME_LENGTH} знаков`);
  }
  return name;
}

function memberKey(groupId: UserGroupId, userId: UserId): string {
  return `${groupId}:${userId}`;
}

function liveGroups(cache: MockCache): UserGroup[] {
  return [...cache.userGroups.values()].filter(
    (group) => group.workspaceId === cache.activeWorkspaceId && group.archivedAt === null,
  );
}

function requireGroup(cache: MockCache, id: UserGroupId): UserGroup {
  if (systemGroupRole(id) !== null) {
    throw new Error('Системную группу менять нельзя');
  }
  const group = cache.userGroups.get(id);
  if (group === undefined || group.workspaceId !== cache.activeWorkspaceId || group.archivedAt !== null) {
    throw new Error('Группа не найдена');
  }
  return group;
}

function requireActiveUser(cache: MockCache, id: UserId): void {
  const user = cache.users.get(id);
  if (user === undefined || !user.active) {
    throw new Error('Пользователь не найден или отключён');
  }
}

export function createUserGroup(args: { id: UserGroupId; name: string }): UserGroup {
  const cache = requireCache();
  requireManager(cache);
  const name = requireGroupName(args.name);
  if (cache.userGroups.has(args.id)) {
    throw new Error('Группа с таким идентификатором уже есть');
  }
  const group: UserGroup = {
    id: args.id,
    workspaceId: cache.activeWorkspaceId,
    name,
    icon: null,
    color: null,
    createdBy: cache.me,
    createdAt: cache.nowMs(),
    sortKey: sortKeyLast(liveGroups(cache)),
    archivedAt: null,
  };
  cache.userGroups.set(group.id, group);
  bump(USER_GROUPS_KEY);
  return group;
}

export function renameUserGroup(args: { id: UserGroupId; name: string }): void {
  const cache = requireCache();
  requireManager(cache);
  const group = requireGroup(cache, args.id);
  group.name = requireGroupName(args.name);
  bump(USER_GROUPS_KEY);
}

export function setUserGroupAppearance(args: {
  id: UserGroupId;
  icon?: LucideIconName | null;
  color?: HexColor | null;
}): void {
  const cache = requireCache();
  requireManager(cache);
  const group = requireGroup(cache, args.id);
  if (args.icon !== undefined) group.icon = args.icon;
  if (args.color !== undefined) group.color = args.color;
  bump(USER_GROUPS_KEY);
}

export function moveUserGroup(args: { id: UserGroupId; afterId: UserGroupId | null }): void {
  const cache = requireCache();
  requireManager(cache);
  const group = requireGroup(cache, args.id);
  const siblings = liveGroups(cache).filter((other) => other.id !== group.id);
  group.sortKey = sortKeyAfter(siblings, args.afterId);
  bump(USER_GROUPS_KEY);
}

export function removeUserGroup(args: { id: UserGroupId }): void {
  const cache = requireCache();
  requireManager(cache);
  const group = requireGroup(cache, args.id);
  for (const [key, member] of cache.userGroupMembers) {
    if (member.groupId === group.id) {
      cache.userGroupMembers.delete(key);
    }
  }
  cache.userGroups.delete(group.id);
  bump(USER_GROUPS_KEY);
}

function requireAdmin(cache: MockCache): void {
  if (cache.users.get(cache.me)?.role !== 'admin') {
    throw new Error('Это действие доступно только администратору');
  }
}

function changeUserRole(cache: MockCache, userId: UserId, role: UserRole): void {
  requireAdmin(cache);
  const user = cache.users.get(userId);
  if (user === undefined) {
    throw new Error('Пользователь не найден');
  }
  if (user.role !== role) {
    setUserRole({ id: userId, role });
    bump(USER_GROUPS_KEY);
  }
}

export function addUserToGroup(args: { groupId: UserGroupId; userId: UserId }): void {
  const cache = requireCache();
  const role = systemGroupRole(args.groupId);
  if (role !== null) {
    requireActiveUser(cache, args.userId);
    changeUserRole(cache, args.userId, role);
    return;
  }
  requireManager(cache);
  const group = requireGroup(cache, args.groupId);
  const key = memberKey(group.id, args.userId);
  if (cache.userGroupMembers.has(key)) {
    return;
  }
  requireActiveUser(cache, args.userId);
  const member: UserGroupMember = {
    groupId: group.id,
    userId: args.userId,
    addedBy: cache.me,
    addedAt: cache.nowMs(),
  };
  cache.userGroupMembers.set(key, member);
  bump(USER_GROUPS_KEY);
}

export function removeUserFromGroup(args: { groupId: UserGroupId; userId: UserId }): void {
  const cache = requireCache();
  const role = systemGroupRole(args.groupId);
  if (role === 'admin') {
    changeUserRole(cache, args.userId, 'member');
    return;
  }
  if (role === 'manager') {
    requireAdmin(cache);
    if (cache.users.get(args.userId)?.role === 'manager') {
      changeUserRole(cache, args.userId, 'member');
    }
    return;
  }
  if (role === 'member') {
    throw new Error('Человек всегда в одной из ролей: участник, менеджер или администратор');
  }
  requireManager(cache);
  const group = requireGroup(cache, args.groupId);
  if (cache.userGroupMembers.delete(memberKey(group.id, args.userId))) {
    bump(USER_GROUPS_KEY);
  }
}
